import { useJsApiLoader } from '@react-google-maps/api';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

// places et visualization sont importées par les modules qui en ont besoin
// (recherche, heatmap) : elles ne pèsent plus sur les autres cartes.
const GOOGLE_MAP_LIBRARIES: ['marker'] = ['marker'];

// Le script est préchargé en tâche de fond après ce délai, pour ne pas
// concurrencer le démarrage de l'application tout en étant prêt quand une
// carte s'affiche ensuite.
const PRELOAD_DELAY_MS = 3000;

type MapsProviderContextType = {
  isLoaded: boolean;
  loadError: Error | undefined;
  requestLoad: () => void;
};

const MapsProviderContext = createContext<MapsProviderContextType | null>(null);

export const useMapsProvider = () => {
  const context = useContext(MapsProviderContext);
  if (!context) {
    throw new Error('useMapsProvider must be used within MapsProvider');
  }
  const { isLoaded, loadError, requestLoad } = context;

  useEffect(() => {
    requestLoad();
  }, [requestLoad]);

  return { isLoaded, loadError };
};

export type MapsProviderProps = {
  token: string;
  mapId?: string | null;
  children: React.ReactNode;
};

const MapsApiLoader = ({
  token,
  mapId,
  onLoadedChange,
  onErrorChange,
}: Omit<MapsProviderProps, 'children'> & {
  onLoadedChange: (isLoaded: boolean) => void;
  onErrorChange: (loadError: Error | undefined) => void;
}) => {
  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: token,
    libraries: GOOGLE_MAP_LIBRARIES,
    mapIds: (mapId && [mapId]) || undefined,
  });

  useEffect(() => {
    onLoadedChange(isLoaded);
  }, [isLoaded, onLoadedChange]);

  useEffect(() => {
    onErrorChange(loadError);
  }, [loadError, onErrorChange]);

  return null;
};

export const MapsProvider = ({ token, mapId, children }: MapsProviderProps) => {
  const [isRequested, setIsRequested] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadError, setLoadError] = useState<Error | undefined>(undefined);

  const requestLoad = useCallback(() => setIsRequested(true), []);

  useEffect(() => {
    let idleId: number | undefined;
    const timeoutId = window.setTimeout(() => {
      if ('requestIdleCallback' in window) {
        idleId = window.requestIdleCallback(requestLoad);
      } else {
        requestLoad();
      }
    }, PRELOAD_DELAY_MS);

    return () => {
      window.clearTimeout(timeoutId);
      if (idleId !== undefined) window.cancelIdleCallback(idleId);
    };
  }, [requestLoad]);

  const value = useMemo(
    () => ({ isLoaded, loadError, requestLoad }),
    [isLoaded, loadError, requestLoad],
  );

  return (
    <MapsProviderContext.Provider value={value}>
      {isRequested && (
        <MapsApiLoader
          token={token}
          mapId={mapId}
          onLoadedChange={setIsLoaded}
          onErrorChange={setLoadError}
        />
      )}
      {children}
    </MapsProviderContext.Provider>
  );
};
